/* ═══════════════════════════════════════════════════════════════════════════
   WISHLIST — auth-gated, per-user storage, cross-component sync
   ───────────────────────────────────────────────────────────────
   How real OTAs handle wishlist (Booking, Airbnb, Hotels.com, Expedia):
     1. The heart icon is ALWAYS visible — visual affordance for the feature.
     2. Tapping the heart while SIGNED OUT does NOT save anything to storage;
        instead it opens a sign-in modal/sheet ("Sign in to save your
        favourites"). After successful sign-in, the action completes.
     3. Saved items are tied to the user's account. Two different users on
        the same browser see different wishlists. Signing out hides them.
     4. The wishlist page shows a sign-in prompt for guests, not the empty
        wishlist UI.

   This module implements that flow:
     • Storage keys are SCOPED to the user's email — `hm_wishlist_v2__<email>`
       and `hm_wishlist__<email>` (v1 legacy). Guests have NO key at all —
       they can't save anything, so there's nothing to store.
     • `useWishlistSync(id)` returns a `[liked, setLiked]` pair that's
       reactive across components AND across the auth state (sign in → all
       cards refresh their hearts; sign out → all hearts clear).
     • `useToggleWishlist(id, meta)` is the high-level hook for cards —
       handles the auth gate automatically: not signed in → fires the
       `hm-request-sign-in` event (caught by SignInGateway, which opens
       the sheet); signed in → writes to user-scoped storage + broadcasts.
     • `clearWishlist()` and `removeFromWishlistV2(key)` also need the user's
       email to know which keys to scrub — they're now hooks/functions that
       take a `userEmail` parameter.

   This eliminates the old "guest can save anonymously then lose it on
   login" UX. The wishlist is always personal and durable.
   ═══════════════════════════════════════════════════════════════════════════ */

import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

/* ── Storage keys ────────────────────────────────────────────────────────── */
const V1_BASE = 'hm_wishlist';
const V2_BASE = 'hm_wishlist_v2';

/** Get the per-user storage key. Returns null when the user has no email
 *  (i.e., not signed in) — callers must short-circuit on null because
 *  guests don't have their own storage. */
export const getWishlistKeys = (email) => {
    if (!email) return { v1: null, v2: null };
    return {
        v1: `${V1_BASE}__${email}`,
        v2: `${V2_BASE}__${email}`,
    };
};

/* ── Events ──────────────────────────────────────────────────────────────── */
export const WISHLIST_EVENT       = 'hm-wishlist-change';
export const SIGN_IN_REQUEST_EVENT = 'hm-request-sign-in';

/** Broadcast that the wishlist changed — every mounted card's
 *  `useWishlistSync` listens and re-reads its liked state. */
export const broadcastWishlistChange = () => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent(WISHLIST_EVENT));
};

/** Request the global sign-in sheet to open. Picked up by SignInGateway
 *  at the app root. Pass an optional `intent` so the sheet can tailor the
 *  copy (e.g. "Sign in to save your favourites" vs default). */
export const requestSignIn = (intent = 'wishlist') => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent(SIGN_IN_REQUEST_EVENT, { detail: { intent } }));
};

/* ── Pure read/write helpers (need the user's email) ─────────────────────── */
const readV1Set = (email) => {
    const { v1 } = getWishlistKeys(email);
    if (!v1) return new Set();
    try { return new Set(JSON.parse(localStorage.getItem(v1) || '[]').map(String)); }
    catch { return new Set(); }
};

// Read-only membership check with the parsed set cached per stored value.
// Every card subscribes to wishlist changes; each used to JSON.parse the whole
// list on every change (and on mount) — N parses for N cards. The set handed
// out by readV1Set() stays a fresh copy, because callers mutate it.
let _v1Cache = { key: null, raw: null, set: new Set() };
const hasInV1 = (email, identifier) => {
    const { v1 } = getWishlistKeys(email);
    if (!v1) return false;
    let raw = null;
    try { raw = localStorage.getItem(v1); } catch {}
    if (_v1Cache.key !== v1 || _v1Cache.raw !== raw) {
        let set;
        try { set = new Set(JSON.parse(raw || '[]').map(String)); } catch { set = new Set(); }
        _v1Cache = { key: v1, raw, set };
    }
    return _v1Cache.set.has(String(identifier));
};

const writeV1Set = (email, set) => {
    const { v1 } = getWishlistKeys(email);
    if (!v1) return;
    try { localStorage.setItem(v1, JSON.stringify(Array.from(set))); } catch {}
};

const readV2Map = (email) => {
    const { v2 } = getWishlistKeys(email);
    if (!v2) return {};
    try { return JSON.parse(localStorage.getItem(v2) || '{}'); }
    catch { return {}; }
};

const writeV2Map = (email, map) => {
    const { v2 } = getWishlistKeys(email);
    if (!v2) return;
    try { localStorage.setItem(v2, JSON.stringify(map)); } catch {}
};

/** Remove a single item from BOTH v1 and v2 for the given user. */
export const removeFromWishlistV2 = (key, email) => {
    if (!email || !key) return;
    const map = readV2Map(email);
    const item = map[key];
    delete map[key];
    writeV2Map(email, map);
    if (item) {
        const v1set = readV1Set(email);
        v1set.delete(String(item.name || ''));
        v1set.delete(String(item.hotelId || ''));
        v1set.delete(String(key));
        writeV1Set(email, v1set);
    }
    broadcastWishlistChange();
};

/** Clear the entire wishlist for the given user (called by WishlistPage's
 *  "Clear all" confirmation). */
export const clearWishlist = (email) => {
    if (!email) return;
    const { v1, v2 } = getWishlistKeys(email);
    try {
        if (v1) localStorage.removeItem(v1);
        if (v2) localStorage.removeItem(v2);
    } catch {}
    broadcastWishlistChange();
};

/** Read the full wishlist map for the given user. */
export const readWishlistV2 = (email) => readV2Map(email);

/* ─── Hook: which user is currently signed in? ────────────────────────────
   AuthContext exposes both `user` (admin profile) and `userSession`
   (customer Google session). Either grants wishlist access; we return the
   first match's email. Returns null when the user is a guest. */
export const useCurrentUserEmail = () => {
    const auth = useAuth() || {};
    return auth.userSession?.email || auth.user?.email || null;
};

/* ─── Hook: subscribe `liked` state to wishlist changes (user-aware) ────── */
export const useWishlistSync = (identifier) => {
    const email = useCurrentUserEmail();
    const [liked, setLiked] = useState(false);

    useEffect(() => {
        // Guest → never liked (storage doesn't exist for them).
        if (!email) {
            setLiked(false);
            return undefined;
        }
        const refresh = () => {
            if (!identifier) { setLiked(false); return; }
            setLiked(hasInV1(email, identifier));
        };
        refresh();
        window.addEventListener(WISHLIST_EVENT, refresh);
        window.addEventListener('storage', refresh);
        return () => {
            window.removeEventListener(WISHLIST_EVENT, refresh);
            window.removeEventListener('storage', refresh);
        };
    }, [identifier, email]);

    return [liked, setLiked];
};

/* ─── Hook: high-level toggle for a card. Handles the auth gate. ──────────
   Usage in a HotelCard:

       const [liked, toggleLiked] = useToggleWishlist(name, {
           type: 'ratehawk', hotelId, name, location, price, ...
       });
       <button onClick={toggleLiked}><Heart ... /></button>

   Behavior:
     • Guest taps → SignInGateway opens. NO storage write. Heart stays off.
     • Signed-in taps → writes to user-scoped v1+v2 → broadcasts → heart flips.
*/
export const useToggleWishlist = (identifier, itemMeta) => {
    const email = useCurrentUserEmail();
    const [liked, setLiked] = useWishlistSync(identifier);

    const toggleLiked = useCallback((e) => {
        if (e && e.stopPropagation) e.stopPropagation();
        if (!identifier) return;

        // Auth gate — guests get the sign-in sheet, not a storage write.
        if (!email) {
            requestSignIn('wishlist');
            return;
        }

        const next = !liked;
        // v1 — flat set keyed by name (legacy compat)
        const v1set = readV1Set(email);
        if (next) v1set.add(String(identifier));
        else v1set.delete(String(identifier));
        writeV1Set(email, v1set);

        // v2 — rich object map (used by the WishlistPage)
        const v2map = readV2Map(email);
        if (next) {
            v2map[String(identifier)] = { ...(itemMeta || {}) };
        } else {
            delete v2map[String(identifier)];
        }
        writeV2Map(email, v2map);

        setLiked(next);
        broadcastWishlistChange();
    }, [email, identifier, itemMeta, liked, setLiked]);

    return [liked, toggleLiked];
};

/* ─── Compatibility shim — bare boolean check used by some legacy code.
       Without `useAuth`, this returns false (safe default). */
export const isInWishlist = () => false;
